import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { unassignAllServers } from 'restaurantAdmin/floorPlans/apiHelpers';
import { Button, ButtonVariants } from '@components/button/Button';
import { Icon } from '@components/icon/Icon';
import { Modal } from '@components/modal/Modal';
import { ModalActions } from '@components/modal/ModalActions';
import { useIsOpen } from '@shared/hooks/useIsOpen';
import cx from 'classnames';
import { partition } from 'lodash-es';
import { useId } from 'react';
import typography from '~styles/typography.scss';
import { useServiceSearchParams } from '../../hooks/useServiceSearchParams';
import { type Server } from './apiHelpers';
import { ServerBadge } from './ServerBadge';
import { useServerFormContext } from './ServerFormContext';
import styles from './ServersList.scss';
import { useServers } from './hooks';

export const ServersList = () => {
  const { id: restaurantId } = useRestaurant();
  const { openServerDrawer } = useServiceSearchParams();
  const { data: servers, refetch } = useServers();
  const { setSelectedServer } = useServerFormContext();
  const {
    isOpen: isConfirmUnassignAllServersModalOpen,
    open: openConfirmUnassignAllServersModal,
    close: closeConfirmUnassignAllServersModal,
  } = useIsOpen();

  const editServer = (server: Server) => {
    setSelectedServer(server);
    openServerDrawer();
  };

  const onClickConfirmUnassignAllServers = () => {
    closeConfirmUnassignAllServersModal();
    void (async () => {
      await unassignAllServers(restaurantId);
      refetch();
    })();
  };

  const [assignedServers, unassignedServers] = partition(
    servers,
    (server) => server.floorPlanTables.length > 0,
  );

  return (
    <div className={styles.container}>
      <ServersListSection
        title="Assigned Servers"
        servers={assignedServers}
        showCovers={true}
        testid="assigned"
        onServerClick={editServer}
      />
      <Button
        className={styles.unassignAllServersButton}
        isDisabled={assignedServers.length === 0}
        label="Unassign All Servers"
        onClick={openConfirmUnassignAllServersModal}
        variant={ButtonVariants.Tertiary}
      />
      <ServersListSection
        title="Unassigned Servers"
        servers={unassignedServers}
        showCovers={false}
        testid="unassigned"
        onServerClick={editServer}
      />
      <button
        className={cx(typography.t1, styles.newServerButton)}
        onClick={openServerDrawer}
        type="button"
      >
        Create New Server
        <Icon name="plus" />
      </button>
      <Modal
        isOpen={isConfirmUnassignAllServersModalOpen}
        onClose={closeConfirmUnassignAllServersModal}
        title="Unassign All Servers"
        subtitle="Click confirm to remove all server table assignments."
      >
        <ModalActions>
          <Button
            label="Cancel"
            onClick={closeConfirmUnassignAllServersModal}
            variant={ButtonVariants.Tertiary}
          />
          <Button
            label="Confirm"
            onClick={onClickConfirmUnassignAllServers}
            variant={ButtonVariants.Primary}
          />
        </ModalActions>
      </Modal>
    </div>
  );
};

const ServersListSection = ({
  title,
  servers,
  showCovers,
  testid,
  onServerClick,
}: {
  title: string;
  servers: Server[];
  showCovers: boolean;
  testid: string;
  onServerClick: (server: Server) => void;
}) => {
  const headingId = useId();
  return (
    <>
      <hgroup
        className={styles.serverGroupHeading}
        data-testid={`${testid}-hgroup`}
      >
        <h4 className={typography.h6} id={headingId}>
          {title}
        </h4>
        {showCovers && <span className={typography.t1}>Covers</span>}
      </hgroup>
      <ul className={styles.serversList} aria-labelledby={headingId}>
        {servers.map((server) => (
          <ServerListItem
            key={server.id}
            server={server}
            onClick={onServerClick}
            showCovers={showCovers}
          />
        ))}
      </ul>
    </>
  );
};

const ServerListItem = ({
  server,
  showCovers,
  onClick,
}: {
  server: Server;
  showCovers: boolean;
  onClick: (server: Server) => void;
}) => (
  <li className={styles.serverListItem} onClick={() => onClick(server)}>
    <ServerBadge
      serverName={server.name}
      {...(!!server.floorPlanTables.length && { badgeColor: server.hexColor })}
    />
    <span className={cx(typography.h8, styles.serverName)} data-testid="name">
      {server.name}
    </span>
    {!server.floorPlanTables.length && <Icon name="chevron" />}
    {showCovers && (
      <>
        {' '}
        <span className={cx(typography.t2, styles.covers)}>
          {server.activeCoversCount}
        </span>
      </>
    )}
  </li>
);
